// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import AlertCircle from "@store/Library/Components/Toolkit/Icons/AlertCircle/AlertCircleLine";
import { loadingDotsStyles } from "@store/Themes/common/mixins/typography";
import useSupportPageLink from "@store/Framework/Common/Hooks/useSupportPageLink";
import tryJsonParse from "@store/Framework/Common/Utilities/tryJsonParse";
import { LeadTimeEntryModel, LeadTimeModel } from "@store/Framework/Types/ApiModels";
import Calendar from "@store/Library/Components/Icons/Calendar";
import Status, { StatusProps } from "@store/Library/Components/Status";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useMemo, FC, useContext } from "react";
import { css } from "styled-components";

export interface ProductLeadTimePresentationProps extends ExpandStyles<ProductLeadTimeStyles> {
    displayAsStatus?: boolean;
    displayInfoToolTip?: boolean;
    displayLabel?: boolean;
    displaySeparator?: boolean;
    showShortLeadTimeDesc?: boolean;
}

export interface ProductLeadTimeProps extends ProductLeadTimePresentationProps {
    overrideLT?: LeadTimeModel;
    referenceProductId?: string | null;
}

export interface ProductLeadTimeStyles {
    loadingText?: TypographyPresentationProps;
    wrapper?: InjectableCss;
    componentWrapper?: InjectableCss;
    statusStyles?: StatusProps;
    leadTimeMessage?: LeadTimeMessage;
    calendarTooltipProps?: TooltipPresentationProps;
    additionalInfoLabel?: TypographyPresentationProps;
    additionalInfoText?: TypographyPresentationProps;
    pipeStyles?: TypographyPresentationProps;
}

interface LeadTimeMessage {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
    linkText?: LinkPresentationProps;
}

export const productLeadTimeStyles: ProductLeadTimeStyles = {
    loadingText: {
        css: loadingDotsStyles(),
    },
    statusStyles: {
        extendedStyles: {
            wrapper: {
                css: css`
                    flex-grow: 0;
                    & > div {
                        display: flex;
                        flex-direction: row;
                        align-items: center;
                        gap: ${pxToRem(4)};
                    }
                `,
            },
        },
    },
    componentWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: ${pxToRem(4)};
        `,
    },
    leadTimeMessage: {
        text: {
            css: css`
                font-size: ${x => x.theme.design.typography.size.smaller};
            `,
        },
        linkText: {
            mergeCss: true,
            css: css`
                font-size: ${x => x.theme.design.typography.size.smaller};
                font-weight: ${x => x.theme.design.typography.weight.medium};
            `,
        },
        tooltip: {
            iconProps: {
                size: 16,
                src: AlertCircle,
            },
            mergeCss: true,
            cssOverrides: {
                tooltipWrapper: css`
                    margin: 0;
                `,
            },
        },
    },
    calendarTooltipProps: {
        iconProps: {
            size: 20,
            src: Calendar,
            color: "inherit",
        },
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                margin: 0;
                text-wrap: auto;
            `,
        },
    },
    additionalInfoLabel: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
    additionalInfoText: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    pipeStyles: {
        color: "text.disabled",
        css: css`
            margin: ${pxToRem(0, 6)};
        `,
    },
};

const ProductLeadTime: FC<ProductLeadTimeProps> = ({
    extendedStyles,
    overridedStyles,
    showShortLeadTimeDesc = false,
    overrideLT,
    referenceProductId: referenceProductIdProp,
    displayLabel = true,
    displaySeparator = false,
    displayInfoToolTip = true,
    displayAsStatus = true,
}) => {
    const baseStyles = useExpandProps(productLeadTimeStyles, extendedStyles, overridedStyles);
    const { product, productInfo } = useContext(ProductContext);
    const isProductContext = Boolean(!overrideLT && product && productInfo);

    const supportPageLink = useSupportPageLink("leadTime", product?.id ?? referenceProductIdProp);

    let leadTimes: string | undefined;
    let pricing: ProductPriceDto | undefined;
    let defaultPlantNumber: string | undefined;

    if (isProductContext) {
        leadTimes = product.properties.leadTimes;
        pricing = productInfo.pricing;
        defaultPlantNumber = pricing?.additionalResults?.warehouseName;
    }

    const isPricingFailed = isProductContext && Boolean(productInfo?.failedToLoadPricing);
    const isLoading = isProductContext && !pricing && !isPricingFailed;

    // Receiving data as a JSON string, needs to convert and extract properties
    const parsedLeadTimes = useMemo<LeadTimeModel[] | undefined>(
        () =>
            tryJsonParse<LeadTimeEntryModel[]>(leadTimes)?.map(entry => ({
                isSpecialItem: entry.IsSpecialItem,
                leadTimeDays: entry.LeadTime,
                minimumOrderQuantity: entry.MinimumOrderQuantity,
                plant: entry.Plant,
            })),
        [leadTimes],
    );

    // While pricing is still loading or failed, defaultPlantNumber isn't known - don't guess via parsedLeadTimes[0],
    // or the status would show a real (but possibly wrong-plant) lead time as if it were verified.
    const leadTime =
        isLoading || isPricingFailed
            ? undefined
            : (overrideLT ??
              (defaultPlantNumber ? parsedLeadTimes?.find(x => x.plant === defaultPlantNumber) : parsedLeadTimes?.[0]));

    // A lead time of 0 means "not maintained" - the backend defaults the ProductWarehouse property
    // to 0 and emits the entry anyway when the product has a minimum order quantity.
    const isLeadTimeUnavailable = !leadTime || !(Number(leadTime.leadTimeDays) > 0);

    const styles = useMemo(() => {
        const colorName = isLeadTimeUnavailable ? "neutral" : leadTime?.isSpecialItem ? "warning" : "success";

        return mergeProps<ProductLeadTimeStyles>(baseStyles, {
            componentWrapper: {
                css: css`
                    color: ${getColor(colorName)};
                `,
            },
        });
    }, [baseStyles, isLeadTimeUnavailable, leadTime]);

    if (!leadTimes && isProductContext) {
        return null;
    }

    const isLeadTimeSpecialItem = leadTime?.isSpecialItem ?? false;

    const tooltipText = (
        isLeadTimeSpecialItem
            ? siteMessage("Product_LeadTimeTooltipSpecialProduct")
            : siteMessage("Product_LeadTimeTooltipRegularProduct")
    ) as string;

    const weekWording = leadTime?.leadTimeDays === 1 ? translate("week") : translate("weeks");

    const iconToolTipText = (
        isLeadTimeUnavailable
            ? siteMessage("ProductInfo_LeadTimeIconNaMessage")
            : siteMessage("ProductInfo_LeadTimeIconMessage")
    ) as string;

    // Minimum order quantity is a field of its own (CDP-4430) - it is never part of this text.
    const getLeadTimeText = (): string => {
        if (isLeadTimeUnavailable) {
            return siteMessage("Product_LeadTimeUnavailable") as string;
        }

        return `${String(leadTime!.leadTimeDays)} ${weekWording}`;
    };

    const getShortLeadTimeText = (): string => {
        if (isLeadTimeUnavailable) {
            return translate("Contact Us");
        }

        return `${String(leadTime!.leadTimeDays)} ${weekWording}`;
    };

    const leadTimeDescriptionText = showShortLeadTimeDesc ? getShortLeadTimeText() : getLeadTimeText();

    const statusType = isLeadTimeUnavailable ? undefined : isLeadTimeSpecialItem ? "yellow" : "green";

    const ShortUnavailableLeadTimeDesc = () => {
        return supportPageLink ? (
            <Link {...styles.leadTimeMessage?.linkText} href={supportPageLink}>
                {leadTimeDescriptionText}
            </Link>
        ) : (
            <Typography {...styles.leadTimeMessage?.text}>{leadTimeDescriptionText}</Typography>
        );
    };

    const LongUnavailableLeadTimeDesc = () => {
        return (
            <Typography {...styles.leadTimeMessage?.text}>
                {supportPageLink ? (
                    <Link {...styles.leadTimeMessage?.linkText} href={supportPageLink}>
                        {translate("Contact Us")}
                    </Link>
                ) : (
                    translate("Contact Us")
                )}{" "}
                {translate("for latest Lead Time")}
            </Typography>
        );
    };

    const LeadTimeValue = () => (
        <>
            {isLeadTimeUnavailable ? (
                // Lead Time Unavailable, display a Contact Us link in short lead time description mode
                showShortLeadTimeDesc ? (
                    <ShortUnavailableLeadTimeDesc />
                ) : (
                    <LongUnavailableLeadTimeDesc />
                )
            ) : (
                // Lead Time Available
                <Typography {...styles.leadTimeMessage?.text}>
                    <Typography {...styles.additionalInfoLabel}>
                        {displayLabel && `${translate("{0}:", translate("Lead Time"))} `}
                    </Typography>
                    <Typography {...styles.additionalInfoText}>{leadTimeDescriptionText}</Typography>
                </Typography>
            )}
            {displayInfoToolTip && <Tooltip {...styles.leadTimeMessage?.tooltip} text={tooltipText} />}
        </>
    );

    const WrapperComponent = ({ children }: React.PropsWithChildren<{}>) => {
        return displayAsStatus ? (
            <Status type={statusType} {...styles.statusStyles}>
                {children}
            </Status>
        ) : (
            <StyledWrapper {...styles.componentWrapper}>{children}</StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="product_lead_time">
            {displaySeparator && <Typography {...styles.pipeStyles}>|</Typography>}
            <WrapperComponent>
                <Tooltip {...styles.calendarTooltipProps} text={iconToolTipText} />
                {isLoading ? <Typography {...styles.loadingText}>&nbsp;</Typography> : <LeadTimeValue />}
            </WrapperComponent>
        </StyledWrapper>
    );
};

export default ProductLeadTime;
